import React, { useState, useEffect } from 'react';
import { User, Mail, Phone } from 'lucide-react';
import { ContactService } from '../services/contactService';
import { ClientService } from '../services/clientService';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, Toggle } from './ui';

const NewContactModal = ({ isOpen, onClose, onContactCreated, defaultClientId, zIndex }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    role: '',
    clientId: defaultClientId || '',
    billing: false,
    notes: '',
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'Project Management', label: 'Project Management' },
    { value: 'Owner/President', label: 'Owner/President' },
    { value: 'Accounts Payable', label: 'Accounts Payable' },
    { value: 'Admin', label: 'Admin' },
    { value: 'Field Staff', label: 'Field Staff' },
    { value: 'Estimator', label: 'Estimator' },
  ];

  useEffect(() => {
    if (isOpen) {
      loadClients();
      // Set default client ID when modal opens
      if (defaultClientId) {
        setFormData(prev => ({ ...prev, clientId: defaultClientId }));
      }
    }
  }, [isOpen, defaultClientId]);

  const loadClients = async () => {
    try {
      const data = await ClientService.getAllClients();
      // Convert to dropdown format
      const clientOptions = data.map((client) => ({
        value: client.id,
        label: client.name,
      }));
      setClients(clientOptions);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.clientId) {
      newErrors.clientId = "Client is required";
    }

    if (!formData.role.trim()) {
      newErrors.role = "Role is required";
    }


    // Email validation if provided
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = "Invalid email format";
    }

    // Phone validation if provided
    if (formData.phoneNumber.trim()) {
      const cleaned = formData.phoneNumber.replace(/\D/g, "");
      if (cleaned.length !== 10) {
        newErrors.phoneNumber = "Phone number must be 10 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const newContact = await ContactService.createContact({
        ...formData,
        clientId: formData.clientId || null,
      });

      onContactCreated(newContact);
      handleClose();
    } catch (error) {
      console.error("Error creating contact:", error);
      setErrors({ submit: "Failed to create contact. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      role: "",
      clientId: "",
      billing: false,
      notes: "",
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear related errors
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

  };

  const formatPhoneInput = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setFormData((prev) => ({ ...prev, phoneNumber: formatted }));

    // Clear errors
    if (errors.phoneNumber) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        return newErrors;
      });
    }
  };

  return (
    <Modal trackAs="NewContactModal" isOpen={isOpen} onClose={handleClose} size="lg" zIndex={zIndex}>
      {/* Custom Header with Avatar and Title */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-orange)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <User size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              New Contact
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Add a new contact to your client database
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Contact Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Contact Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Name */}
                <Input
                  label="Full Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter contact name"
                  error={errors.name}
                  required
                  icon={<User size={16} />}
                />

                {/* Email */}
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contact@example.com"
                  error={errors.email}
                  icon={<Mail size={16} />}
                />

                {/* Phone */}
                <Input
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  error={errors.phoneNumber}
                  icon={<Phone size={16} />}
                />

                {/* Role */}
                <Dropdown
                  label="Role / Position"
                  options={roleOptions}
                  value={formData.role}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, role: value }));
                    if (errors.role) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.role;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select a role"
                  error={errors.role}
                />
              </div>
            </div>

            {/* Right Column: Organization */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Organization
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Client */}
                <Dropdown
                  label="Client Organization"
                  options={clients}
                  value={formData.clientId}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, clientId: value }));
                    if (errors.clientId) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.clientId;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select a client"
                  searchable
                  error={errors.clientId}
                  loading={clients.length === 0}
                />

                {/* Billing toggle */}
                <Toggle
                  label="Billing Contact"
                  checked={formData.billing}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, billing: e.target.checked }));
                  }}
                  helperText="Enable if this contact handles billing and invoices"
                />

                {/* Notes */}
                <div>
                  <label
                    htmlFor="notes"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Add any additional notes about this contact..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--cta-blue)';
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--gray-600)';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)',
              width: '100%',
            }}
          >
            <Button variant="ghost" onClick={handleClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={loading} disabled={loading}>
              {loading ? 'Creating...' : 'Create Contact'}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default NewContactModal;
